import { ButtonType } from '@ardoq/button';
import type { TraversalBuilderState } from '../types';
import type { EditTraversalCommands } from 'viewpointBuilder/traversals/editTraversalCommands';
import { IconName } from '@ardoq/icons';
import { FormSize, Switch, TextInput } from '@ardoq/forms';
import { ButtonWithDropdown, DropdownOptionType } from '@ardoq/dropdown-menu';
import { viewpointBuilderTracking } from '../viewpointBuilderTracking';
import { Box, FlexBox } from '@ardoq/layout';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { t, type I18nLocale } from '@ardoq/i18n';

type FilterRowProps = {
  triplesSortAndFiltersState: TraversalBuilderState['triplesSortAndFiltersState'];
  commands: EditTraversalCommands;
  isDisabled: boolean;
  i18nLocale: I18nLocale;
  /**
   * The outer "Add hop" popover uses the native Popover API, which paints its
   * whole subtree in the browser's top layer, above all normal-stacking
   * content regardless of z-index. The settings dropdown's own menu is a
   * plain `position: fixed` + z-index element, so once it portals out to
   * document.body (outside that subtree) it paints *behind* the popover
   * instead of above it — invisible, though still fully interactive. Set this
   * to keep the dropdown nested inside the popover's own top-layer subtree.
   */
  disableSettingsMenuPortal?: boolean;
};

export const FilterRow = ({
  triplesSortAndFiltersState,
  commands,
  isDisabled,
  i18nLocale,
  disableSettingsMenuPortal,
}: FilterRowProps) => {
  return (
    <FlexBox gap="small">
      <TextInput
        value={triplesSortAndFiltersState.filterTerm}
        leftIconName={IconName.FILTER_LIST}
        placeholder={t(
          i18nLocale,
          'viewpointBuilder:filterRow.placeholder',
          'Search reference and component types'
        )}
        onValueChange={filterTerm =>
          commands.setSectionFilter({
            filterTerm,
          })
        }
        onClear={() => {
          commands.setSectionFilter({
            filterTerm: '',
          });
        }}
        formSize={FormSize.FULL}
        isDisabled={isDisabled}
      />

      <SettingsDropdown
        triplesSortAndFiltersState={triplesSortAndFiltersState}
        commands={commands}
        isDisabled={isDisabled}
        i18nLocale={i18nLocale}
        disableSettingsMenuPortal={disableSettingsMenuPortal}
      />
    </FlexBox>
  );
};

const SettingsDropdown = ({
  triplesSortAndFiltersState,
  commands,
  isDisabled,
  i18nLocale,
  disableSettingsMenuPortal,
}: FilterRowProps) => {
  return (
    <ButtonWithDropdown
      title={t(i18nLocale, 'viewpointBuilder:filterRow.settings', 'Settings')}
      tooltipText={t(
        i18nLocale,
        'viewpointBuilder:filterRow.settings',
        'Settings'
      )}
      iconName={IconName.SETTINGS}
      alignMenuTo="right"
      dataTestId="traversal-dialog-tripe-sort-order"
      isDisabled={isDisabled}
      disablePortal={disableSettingsMenuPortal}
      options={[
        {
          label: t(
            i18nLocale,
            'viewpointBuilder:filterRow.showPreferences',
            'Show preferences'
          ),
          type: DropdownOptionType.HEADER,
        },
        {
          type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
          onClick: event => event.stopPropagation(),
          content: (
            <div
              key="showOnlyOptionsWithInstanceCountsToggleOption"
              onClick={event => event.stopPropagation()}
            >
              <Box padding="small">
                <Switch
                  name="showOnlyOptionsWithInstanceCounts"
                  isChecked={
                    triplesSortAndFiltersState.showOnlyOptionsWithInstanceCounts
                  }
                  label={t(
                    i18nLocale,
                    'viewpointBuilder:filterRow.hideZeroLinked',
                    'Hide references with 0 linked components'
                  )}
                  onChange={commands.toggleShowOnlyOptionsWithInstanceCounts}
                />
              </Box>
            </div>
          ),
        },
        {
          type: DropdownOptionType.DIVIDER,
        },
        {
          label: t(i18nLocale, 'viewpointBuilder:filterRow.sortBy', 'Sort by'),
          type: DropdownOptionType.HEADER,
        },
        {
          leftIcon: { name: IconName.REFERENCE_TYPE },
          label: t(
            i18nLocale,
            'viewpointBuilder:filterRow.sortBy.referenceTypeAZ',
            'Reference type A-Z'
          ),
          isActive:
            triplesSortAndFiltersState.tripleSortOrder === 'reference_type_a_z',
          onClick: () => {
            commands.setTripleSortOrder({
              tripleSortOrder: 'reference_type_a_z',
            });
            viewpointBuilderTracking.trackSelectReferenceTraversalTripleSortOrder();
          },
        },
        {
          leftIcon: { name: IconName.COMPONENT },
          label: t(
            i18nLocale,
            'viewpointBuilder:filterRow.sortBy.componentTypeAZ',
            'Component type A-Z'
          ),
          isActive:
            triplesSortAndFiltersState.tripleSortOrder === 'component_type_a_z',
          onClick: () => {
            commands.setTripleSortOrder({
              tripleSortOrder: 'component_type_a_z',
            });
            viewpointBuilderTracking.trackSelectComponentTraversalTripleSortOrder();
          },
        },
        {
          leftIcon: { name: IconName.ARROW_DOWNWARD },
          label: t(
            i18nLocale,
            'viewpointBuilder:filterRow.sortBy.descendingInstanceCount',
            'Descending instance count'
          ),
          isActive:
            triplesSortAndFiltersState.tripleSortOrder ===
            'descending_instance_count',
          onClick: () => {
            commands.setTripleSortOrder({
              tripleSortOrder: 'descending_instance_count',
            });
            viewpointBuilderTracking.trackSelectDescendingInstanceCountTraversalTripleSortOrder();
          },
        },
      ]}
      buttonType={ButtonType.GHOST}
    >
      <VisuallyHidden>
        {t(i18nLocale, 'viewpointBuilder:filterRow.settings', 'Settings')}
      </VisuallyHidden>
    </ButtonWithDropdown>
  );
};
